import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    lighten,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useGetJobsQuery } from '../../../../app/Slices/SevenDayApiSlice';
import { Job } from '../../../../app/Slices/SevenDayApiTypes';
import { BookingSetMenu } from '../../../types/set-menu';
import { theme } from '../../../../shared/theme';
import ContractorModal from './ContractorModal';
import moment from 'moment';
import JobList from '../../Contractors/Jobs/JobList';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import { useLocation, useSearchParams } from 'react-router-dom';

type Props = {
    booking: any;
};

export default function ReservationHostsContractors({ booking }: Props) {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const fullScreen = useMediaQuery('(max-width: 600px)');
    const bookingId = booking?.id;

    const location = useLocation();
    const [, setSearchParams] = useSearchParams();

    const { data: jobs } = useGetJobsQuery({ bookingId }, { skip: !bookingId });

    const allJobs = useMemo(
        () => [...(jobs ?? []), ...(booking?.setMenus ?? [])],
        [jobs, booking?.setMenus],
    );

    const [open, setOpen] = useState(false);
    const [formOpen, setFormOpen] = useState(false);

    const handleOpen = () => setOpen(true);

    const selectJobParams = (job: Job | BookingSetMenu) => {
        if (job.id == null) return;

        setFormOpen(false);
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set('selectedJob', String(job.id));
                next.set('provider-type', String(job.providerType));
                return next;
            },
            { replace: true, state: location.state },
        );
    };

    const clearContractorParams = () => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete('selectedJob');
                next.delete('provider-type');
                return next;
            },
            { replace: true, state: location.state },
        );
    };

    const handleClose = () => {
        setOpen(false);
        setFormOpen(false);
        clearContractorParams();
    };

    const handleOpenCreateJob = () => {
        clearContractorParams();
        setFormOpen(true);
    };

    const totalJobs = allJobs.length;

    const getStatusSx = (s: string) => {
        switch (s) {
            case 'approved':
                return {
                    bgcolor: lighten(theme.palette.yellow.main, 0.78),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.yellow.main, 0.35),
                };
            case 'pending':
                return {
                    bgcolor: lighten(theme.palette.primary.main, 0.76),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.primary.main, 0.32),
                };
            case 'cancelled':
            case 'declined':
                return {
                    bgcolor: lighten(theme.palette.error.main, 0.82),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.error.main, 0.3),
                };
            case 'completed':
                return {
                    bgcolor: lighten(theme.palette.info.main, 0.82),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.info.main, 0.3),
                };
            default:
                return {
                    bgcolor: theme.palette.custom.cardActionBackground,
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.custom.lightBorderColor,
                };
        }
    };

    return (
        <>
            <Box
                onClick={handleOpen}
                role='button'
                tabIndex={0}
                sx={{
                    cursor: 'pointer',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                    backgroundColor: theme.palette.custom.cardActionBackground,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    '&:hover': {
                        borderColor: theme.palette.info.main,
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: theme.palette.neutral.main,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        }}
                    >
                        <AssignmentIndIcon
                            fontSize='small'
                            sx={{ opacity: 0.75 }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                            variant='body3'
                            sx={{
                                fontWeight: 800,
                                letterSpacing: 0.2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                flexWrap: 'wrap',
                            }}
                        >
                            Jobs ({totalJobs})
                            <VisibilityOffIcon
                                sx={{ opacity: 0.55 }}
                                fontSize='small'
                            />
                        </Typography>

                        <Typography
                            variant='body3'
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: 12,
                                lineHeight: 1.2,
                            }}
                        >
                            View & manage jobs for this reservation
                        </Typography>
                    </Box>
                </Box>

                <Typography
                    variant='body3'
                    sx={{
                        color: theme.palette.text.secondary,
                        fontSize: 12,
                        whiteSpace: 'nowrap',
                    }}
                >
                    Open
                </Typography>
            </Box>

            <Dialog
                open={open}
                onClose={handleClose}
                fullScreen={fullScreen}
                maxWidth='lg'
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: fullScreen ? 0 : 3,
                        overflow: 'hidden',
                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        display: 'flex',
                        flexDirection: 'column',
                        height: fullScreen ? '100dvh' : 'auto',
                        backgroundColor: theme.palette.neutral.main,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        position: fullScreen ? 'sticky' : 'relative',
                        top: 0,
                        zIndex: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            Jobs
                            <VisibilityOffIcon
                                sx={{ opacity: 0.55 }}
                                fontSize='small'
                            />
                        </Box>

                        <Typography
                            variant='body3'
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: 12,
                            }}
                        >
                            Manage contractor jobs and your own service jobs for
                            this reservation
                        </Typography>

                        <Typography
                            variant='body3'
                            sx={{
                                color: theme.palette.text.secondary,
                                fontSize: 10,
                            }}
                        >
                            Note: Contractor jobs you create here are not
                            visible to guests
                        </Typography>
                    </Box>

                    <IconButton onClick={handleClose} size='small'>
                        <CloseIcon fontSize='small' />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                            p: 1.75,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                            backgroundColor:
                                theme.palette.custom.cardActionBackground,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 900,
                                    letterSpacing: 0.3,
                                    fontSize: 12,
                                    textTransform: 'uppercase',
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                What are contractor jobs?
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: theme.palette.text.primary,
                                    lineHeight: 1.45,
                                }}
                            >
                                Contractor jobs (catering, wellness, etc.) are
                                jobs assigned to external providers linked to
                                this reservation.
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.45,
                                }}
                            >
                                You can create a contractor job here, and track
                                its status.
                            </Typography>
                        </Box>

                        <Button
                            variant='outlined'
                            sx={{
                                height: 44,
                                alignSelf: isMobile ? 'stretch' : 'flex-start',
                                minWidth: isMobile ? '100%' : 160,
                                borderRadius: 2,
                            }}
                            fullWidth={isMobile}
                            onClick={handleOpenCreateJob}
                        >
                            Create job
                        </Button>
                    </Box>

                    {isMobile ? (
                        <Stack spacing={1.25}>
                            {(allJobs ?? []).length ? (
                                (allJobs ?? []).map((job: any) => (
                                    <Box
                                        key={`${job.providerType}-${job.id}`}
                                        onClick={() => selectJobParams(job)}
                                        role='button'
                                        tabIndex={0}
                                        sx={{
                                            cursor: 'pointer',
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                                            backgroundColor:
                                                theme.palette.neutral.main,
                                            p: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1.25,
                                        }}
                                    >
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 900,
                                                    fontSize: 14,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {job?.name ||
                                                    job?.title ||
                                                    job?.contractor?.name ||
                                                    `Job #${job.id}`}
                                            </Typography>

                                            <Box
                                                sx={{
                                                    mt: 0.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 12,
                                                        color: theme.palette
                                                            .text.secondary,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    Job #{job.id}
                                                </Typography>

                                                {job?.dueDate ? (
                                                    <>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                color: theme
                                                                    .palette
                                                                    .text
                                                                    .secondary,
                                                            }}
                                                        >
                                                            •
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                color: theme
                                                                    .palette
                                                                    .text
                                                                    .secondary,
                                                                whiteSpace:
                                                                    'nowrap',
                                                            }}
                                                        >
                                                            Due{' '}
                                                            {moment(
                                                                job.dueDate,
                                                            ).format(
                                                                'DD/MM/YYYY',
                                                            )}
                                                        </Typography>
                                                    </>
                                                ) : null}
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {job?.status ? (
                                                <Chip
                                                    label={camelCaseToTitleCase(
                                                        job.status,
                                                    ).toUpperCase()}
                                                    sx={{
                                                        ...getStatusSx(
                                                            job.status,
                                                        ),
                                                        height: 'auto',
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                    }}
                                                />
                                            ) : null}

                                            <ChevronRightIcon
                                                sx={{ opacity: 0.6 }}
                                            />
                                        </Box>
                                    </Box>
                                ))
                            ) : (
                                <Box sx={{ px: 0.5, py: 1 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        No jobs yet.
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    ) : null}

                    <Box
                        sx={{
                            mt: isMobile ? 0 : 2,
                            width: '100%',
                            display: isMobile ? 'none' : 'block',
                        }}
                    >
                        <JobList
                            booking={booking}
                            onSelectJob={(job) => selectJobParams(job)}
                        />
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        justifyContent: 'space-between',
                        gap: 2,
                        flexWrap: 'wrap',
                        position: fullScreen ? 'sticky' : 'relative',
                        bottom: 0,
                        zIndex: 2,
                    }}
                >
                    <Typography
                        variant='body3'
                        sx={{
                            color: theme.palette.text.secondary,
                            fontSize: 12,
                        }}
                    >
                        Total jobs: {totalJobs}
                    </Typography>

                    <Button
                        variant='outlined'
                        onClick={handleClose}
                        sx={{ borderRadius: 2, minHeight: 44 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <ContractorModal
                id={bookingId}
                open={formOpen}
                startDate={booking?.startDate}
                onClose={() => {
                    setFormOpen(false);
                    clearContractorParams();
                }}
            />
        </>
    );
}
